import { pxToRem } from "@/shared/styles/themes"
import { styled } from "@mui/material"
import { useContext } from "react"
import { useDrop } from "react-dnd"
import { MosaicContext } from "./contextTypes"
import { MosaicDropData, MosaicDropTargetPosition } from "./internalTypes"
import { MosaicDragItem, MosaicDragType } from "./models/mosaicDragType"
import { MosaicDropTargetProps } from "./MosaicDropTarget"

export const RootDropTarget: React.FC<MosaicDropTargetProps> = ({ path, position }) => {
  const { mosaicActions, mosaicId } = useContext(MosaicContext)
  const [{ isOver, draggedMosaicId }, connectDropTarget] = useDrop({
    accept: [MosaicDragType.WINDOW, MosaicDragType.WIDGET_BUTTON, MosaicDragType.TREE_ITEM],
    drop: (item: MosaicDragItem | undefined, _monitor): MosaicDropData => {
      if (!item || mosaicId !== item.mosaicId) return {}

      if (item?.type === MosaicDragType.WIDGET_BUTTON) {
        mosaicActions.onDropCreate(item, position, path)
        return { isNewWindow: true }
      }
      else if (item.type === MosaicDragType.TREE_ITEM) {
        mosaicActions.onDropCreate(item, position, path)
        return { isNewWindow: true }
      }

      return { path, position }
    },
    collect: (monitor) => {
      return ({
        isOver: monitor.isOver(),
        draggedMosaicId: (monitor.getItem() || {}).mosaicId,
      })
    },
  })

  return (
    <>
      <StyledDropTarget
        ref={connectDropTarget}
        style={styleLookup[position]}
      >
      </StyledDropTarget>
      <StyledDropPlaceholder $isOver={isOver && draggedMosaicId === mosaicId} $position={position} />
    </>
  )
}

const styleLookup = {
  'top': {
    top: pxToRem(8),
    left: '40%',
    right: '40%',
    height: pxToRem(8),
    borderRadius: `0 0 ${pxToRem(8)} ${pxToRem(8)}`,
  },
  'bottom': {
    bottom: 0,
    left: '40%',
    right: '40%',
    height: pxToRem(8),
    borderRadius: `${pxToRem(8)} ${pxToRem(8)} 0 0`,
  },
  'left': {
    top: '40%',
    bottom: '40%',
    left: pxToRem(8),
    width: pxToRem(8),
    borderRadius: `0 ${pxToRem(8)} ${pxToRem(8)} 0`,
  },
  'right': {
    top: '40%',
    bottom: '40%',
    right: 0,
    width: pxToRem(8),
    borderRadius: `${pxToRem(8)} 0 0 ${pxToRem(8)}`,
  },
}


const StyledDropTarget = styled('div')`
  position: absolute;
  z-index: 100;
  background: rgba(102, 194, 196, 0.5);
  border: 1px solid #66C2C4;
`

const StyledDropPlaceholder = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isOver' && prop !== '$position',
}) <{ $isOver: boolean, $position: MosaicDropTargetPosition }>`
  visibility: ${({ $isOver }) => ($isOver ? 'visible' : 'hidden')};
  position: absolute;
  z-index: 8;
  top: ${({ $position }) => $position === MosaicDropTargetPosition.BOTTOM ? '50%' : pxToRem(8)};
  bottom: ${({ $position }) => $position === MosaicDropTargetPosition.TOP ? '50%' : 0};
  left: ${({ $position }) => $position === MosaicDropTargetPosition.RIGHT ? '50%' : pxToRem(8)};
  right: ${({ $position }) => $position === MosaicDropTargetPosition.LEFT ? '50%' : 0};
  background-color: ${({ theme }) => theme.palette.custom.widgetDropTarget.backgroundColor};
  border-radius: ${pxToRem(8)};
  border: ${({ theme }) => theme.palette.custom.widgetDropTarget.border};
  opacity: ${({ $isOver }) => ($isOver ? 1 : 0)};
  transition: opacity 0.1s;
  padding: ${props => props.theme.typography.pxToRem(8)};
`